/// <reference path="../../imports.ts" />
/// <reference path="../../Scripts/dashboards/interfaces.ts" />
/// <reference path="../../Scripts/dashboards/canvas.ts" />
/// <reference path="../../Scripts/dashboards/renderers/chart.ts" />
/// <reference path="../../Scripts/dashboards/renderers/widget.ts" />
/// <reference path="../../Scripts/dashboards/designer.ts" />
/// <reference path="../../Scripts/dashboards/globalFilterPane.ts" />

namespace distill.dashboards.publicView {
    interface PublicViewInitArgs {
        shareKey: string;
        dataFetchUrl: string;
        aiQueryUrl: string;
        globalFilterOptionsUrl: string;
        tokenUrl: string;
        exportUrl?: string;
        design: distill.IDesign;
        datasets: distill.dashboards.IDatasetMeta[];
        editUserUrl?: string;
    }

    let _args: PublicViewInitArgs;
    let _currentAccessToken: string | null = null;
    let _lastTokenRefreshTime: number = 0;
    let _tokenExpiryWindowMs = 2.5 * 60 * 1000;

    export function getAccessToken(): string | null {
        return _currentAccessToken;
    }

    export function run(args: PublicViewInitArgs): void {
        _args = args;

        if (args.exportUrl)
            distill.dashboards.exportUrl = args.exportUrl;
        distill.dashboards.isPublicView = true;
        
        const titleBar = args.design.components.find(c => c.type === distill.dashboards.ComponentType.titleBar) as distill.dashboards.IDashboardComponent;
        const refreshInterval = titleBar?.refreshInterval || '5m';
        const refreshMs = distill.dashboards.getIntervalMs(refreshInterval);

        distill.dashboards.designer = new distill.dashboards.DashboardDesigner(args.datasets);

        appn.designerv2.canvas = new distill.dashboards.GridStackCanvas({
            column: 12,
            staticGrid: true,
            float: true,
            disableResize: true,
            disableDrag: true,
            itemClass: 'component',
            cellHeight: 'initial' // Kills the internal auto-loop, preventing scrollbar thrashing
        });

        distill.dashboards.designer.setDesign(args.design);
        appn.designerv2.canvas.render(args.design);

        // Keep cells square on genuine window resizes only. GridStack's auto cell height reacts to
        // any container resize (e.g. the filter pane opening), which thrashes the scrollbar:
        // width -> cell height -> page height -> scrollbar -> width. 'initial' plus this guard
        // breaks that loop.
        let lastWindowWidth = window.innerWidth;

        window.addEventListener('resize', () => {
            if (window.innerWidth === lastWindowWidth)
                return;
            lastWindowWidth = window.innerWidth;

            const canvas = appn.designerv2.canvas as distill.dashboards.GridStackCanvas;
            if (canvas && canvas._grid && canvas._grid.el) {
                const gridWidth = canvas._grid.el.clientWidth;
                const newCellHeight = Math.floor(gridWidth / 12);

                if (newCellHeight > 0)
                    canvas._grid.cellHeight(newCellHeight);
            }
        });

        // Trigger it once on load so the initial dashboard renders with square widgets
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 150);

        distill.dataCache.init(refreshMs * 4);
        distill.dashboards.dataManager.init(args.dataFetchUrl, async () => {
            await ensureValidAccessToken();
            return _currentAccessToken;
        });
        setupRendering();
        setupDataFetching();
        setupGlobalFilterPane(args);

        setInterval(() => {
            distill.eventBus.publish(distill.EVENTS.DATA_REFRESH_REQUESTED, {});
        }, refreshMs);

        distill.eventBus.publish(distill.EVENTS.DATA_REFRESH_REQUESTED, {});
    }

    async function fetchAccessToken(): Promise<void> {
        try {
            const response = await fetch(_args.tokenUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const result = await response.json();
            if (result.success && result.token) {
                _currentAccessToken = result.token;
                _lastTokenRefreshTime = Date.now();
            } else
                clearDashboardData();
        } catch (error) {
            clearDashboardData();
        }
    }

    /// Ensures the access token is present and fresh (refreshing it when near expiry).
    export async function ensureValidAccessToken(): Promise<boolean> {
        const now = Date.now();
        const timeSinceRefresh = now - _lastTokenRefreshTime;

        if (_currentAccessToken && timeSinceRefresh < _tokenExpiryWindowMs)
            return true;

        if (timeSinceRefresh >= _tokenExpiryWindowMs || !_currentAccessToken) {
            await fetchAccessToken();
            return _currentAccessToken !== null;
        }

        return false;
    }

    function clearDashboardData(): void {
        distill.dataCache.clear();
        _currentAccessToken = null;
        _lastTokenRefreshTime = 0;
    }

    const _filterMap = new Map<string, IDashFilterSelection[]>();

    function setupDataFetching(): void {
        async function fetchData(viewNames?: string[] | null, componentId?: string, widgetFilters?: IDashFilterSelection[], pagingWindow?: { offset: number, limit: number } | null) {
            const body: any = {
                accessToken: _currentAccessToken,
                sortOverrides: distill.dashboards.widgets.collectSortOverrides(),
                viewNames: viewNames && viewNames.length > 0 ? viewNames : null
            };
            if (componentId)
                body.componentId = componentId;

            // One filter list: the pane's URL-derived global filters plus the widgets' own
            // column filters.
            const filters = [...globalFilterPane.getAppliedFilters(), ...(widgetFilters || [])];
            if (filters.length > 0)
                body.filters = filters;

            // Server-paged table widgets fetch windows of rows: the triggering payload's window
            // for component-scoped refreshes, or every table's current window on a full refresh.
            const windows = pagingWindow ? [{ componentId: componentId, offset: pagingWindow.offset, limit: pagingWindow.limit }] : dataManager.getTableWindows();
            if (windows.length > 0)
                body.windows = windows;

            return await fetch(_args.dataFetchUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
        }
        distill.eventBus.subscribe(distill.EVENTS.DATA_REFRESH_REQUESTED, async (payload?: { componentId?: string; viewName?: string; filters?: IDashFilterSelection[]; window?: { offset: number, limit: number }; resetTablePaging?: boolean }) => {
            try {
                if (!await ensureValidAccessToken()) {
                    clearDashboardData();
                    return;
                }

                if (payload?.componentId && payload?.filters !== undefined) {
                    if (payload.filters && payload.filters.length > 0)
                        _filterMap.set(payload.componentId, payload.filters);
                    else
                        _filterMap.delete(payload.componentId);
                }

                // A global-filter commit re-runs every view under new filters — table windows
                // restart from the first page (their cached rows mean nothing under the new
                // filters). Auto-refresh keeps the current windows; the refetch itself renews
                // the stale rows.
                if (payload?.resetTablePaging)
                    dataManager.resetTableWindows();

                const targetCompId = payload?.componentId;
                const filtersToSend = targetCompId
                    ? (_filterMap.get(targetCompId) || [])
                    : Array.from(_filterMap.values()).flat();

                const viewNames = payload?.viewName ? [payload.viewName] : null;

                console.log("fetching data...");
                let response = await fetchData(viewNames, targetCompId, filtersToSend, payload?.window);
                if ([401, 403].includes(response.status)) {
                    await fetchAccessToken();
                    if (_currentAccessToken)
                        response = await fetchData(viewNames, targetCompId, filtersToSend, payload?.window);
                }

                const result: distill.dashboards.IDataQueryResponse = await response.json();
                if (result.Error)
                    throw new Error(`Query result error! Message: ${result.Error}`);

                distill.eventBus.publish(distill.EVENTS.DATA_FETCH_SUCCESS, result);
            } catch (error) {
                console.error("fetching data failed:", error);
                const httpError = error as any;
                if (httpError.message?.includes('401') || httpError.message?.toLowerCase().includes('unauthorized'))
                    clearDashboardData();
            }
        });

        function publishQueryResult(componentId: string, response: IAskQueryResponse, success: boolean): void {
            eventBus.publish(DASH_EVENTS.AI_QUERY_RESULT, {
                componentId,
                response,
                success,
                upgradeUrl: _args.editUserUrl,
            });
        }

        async function fetchChat(payload: IAiQuerySendPayload){
            return await fetch(_args.aiQueryUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    accessToken: _currentAccessToken,
                    componentId: payload.component.id,
                    question: payload.userQuery,
                    responseMarkdownFormatted: true,
                    history: payload.history || [],
                    historySignature: payload.historySignature
                })
            });
        }
        eventBus.subscribe(DASH_EVENTS.AI_QUERY_SEND, async (payload: IAiQuerySendPayload) => {
            try {
                if (!await ensureValidAccessToken()) {
                    publishQueryResult(payload.component.id, { Answer: T('Unable to load your chat session. Please refresh the page.'), Charts: [] }, false);
                    return;
                }

                let response = await fetchChat(payload);
                if ([401, 403].includes(response.status) && _currentAccessToken) {
                    await fetchAccessToken();

                    if (_currentAccessToken)
                        response = await fetchChat(payload);
                }

                if (!response.ok) {
                    let apiMsg = '';
                    try {
                        apiMsg = (await response.json())?.responseStatus?.message || '';
                    } catch { /* ignore */ }
                    publishQueryResult(payload.component.id, {
                        Answer: apiMsg === 'Question is required.' ? T('Please ask a question.') : (apiMsg || T('Sorry, something went wrong. Please try again.')),
                        Charts: []
                    }, false);
                    return;
                }

                const json = await response.json();
                const answer = json?.Answer || T(`To use this widget, you'll need some active artifacts.`);
                publishQueryResult(payload.component.id, { Answer: answer, Charts: json?.Charts || [], ErrorType: json?.ErrorType, Turns: json?.Turns, HistorySignature: json?.HistorySignature }, true);
            } catch (error) {
                publishQueryResult(payload.component.id, { Answer: T('Sorry, something went wrong. Please try again.'), Charts: [] }, false);
            }
        });
    }

    function setupGlobalFilterPane(args: PublicViewInitArgs): void {
        globalFilterPane.init({
            design: args.design as distill.dashboards.IDashboardDesign,
            fetchGlobalFilterOptions: async (filter, search) => {
                if (!filter.sourceDatasetId || !filter.displayColumn || !filter.valueColumn)
                    return [];

                if (!await ensureValidAccessToken())
                    return [];

                try {
                    // The service resolves the source dataset and display/value columns from
                    // the design's declared filter — the client only identifies the filter.
                    // The list is the filter's own values, never narrowed by the other applied filters: how a filter combines
                    // with a component (AND, OR) is that component's business.
                    const response = await fetch(args.globalFilterOptionsUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            accessToken: _currentAccessToken,
                            filterId: filter.id,
                            search: search || ''
                        })
                    });

                    if (!response.ok)
                        return [];

                    const result = await response.json();
                    return result && result.Options
                        ? result.Options.map((o: { Display: string; Value: string; Count?: number }) => ({ display: o.Display, value: o.Value, count: o.Count || 0 }))
                        : [];
                } catch {
                    return [];
                }
            }
        });
    }

    function setupRendering(){
        eventBus.subscribe(EVENTS.DATA_FETCH_SUCCESS, (result: IDataQueryResponse) => {
            const refreshedComponentIds = new Set<string>();
            result.Views.forEach(datasetResult => {
                const cacheKey = appn.utils.normalizeIdentifier(datasetResult.Name);
                const componentId = cacheKey.split('.')[0];

                if (appn.utils.hasValue(datasetResult.Error))
                    console.error(`Could not fetch data for ${cacheKey}`, datasetResult.Error);

                if (componentId){
                    refreshedComponentIds.add(componentId);
                    dataCache.set(cacheKey, datasetResult);
                }
            });

            if (!result || !result.Views)
                return;

            refreshedComponentIds.forEach(id => {
                const component = dashboards.designer.getComponent(id);
                if (!component)
                    return;

                const definition = componentManager.getDefinition(component.type);
                if (!definition || !definition.render)
                    return;

                const element = $(`#${id}`);
                if (!element.length)
                    return;

                definition.render(element, component);
            });
        });
    }
}
